import React, { useEffect, useState } from "react";
import { Popup } from "../Popup/Popup.jsx";

/**
 * Avatar — A component that displays a user's initials in a circle or their profile image.
 *
 * It calculates initials from the provided name (up to two words).
 * If a profile image is provided and valid, it renders the image.
 * Clicking on the profile image can open a larger view in a popup.
 *
 * @param {string}  name                 - The display name to get initials from.
 * @param {number}  width                - Diameter of the circle (default: 48).
 * @param {object}  textStyle            - Custom styles for the initials text (deprecated in favor of SCSS).
 * @param {string}  profile_image        - URL of the profile image.
 * @param {boolean} fullProfileImageView - Whether to show the large image popup on click (default: true).
 */
export const Avatar = ({
  name,
  width = 48,
  textStyle = {},
  textClassName = "body-m-semibold",
  profile_image,
  fullProfileImageView = true,
  isSquareOnHover = false,
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [validImage, setValidImage] = useState(!!profile_image);

  useEffect(() => {
    setValidImage(!!profile_image);
  }, [profile_image]);

  if (!name) {
    return null;
  }

  // Calculate initials (e.g., "John Doe" -> "JD")
  const trimmedName = name.trim().toUpperCase();
  const nameParts = trimmedName.split(" ");
  let initials = nameParts[0]?.[0] || "";
  if (nameParts[1]?.[0]) {
    initials += nameParts[1][0];
  }

  const circleStyle = {
    width: width,
    height: width,
    minWidth: width,
    minHeight: width,
  };

  const renderPopup = () => (
    <Popup
      name=""
      handleOnClosePopup={() => setShowPopup(false)}
      maxWidth={360}
      customContentStyle={{ display: "flex", justifyContent: "center", padding: 0 }}
    >
      <div className="avatar-popup-content">
        <img src={profile_image} alt={initials} />
      </div>
    </Popup>
  );

  const containerClasses = `avatar-container ${validImage ? "has-image" : ""} ${
    isSquareOnHover ? "square-on-hover" : ""
  }`;

  if (validImage) {
    return (
      <>
        <div
          className={containerClasses}
          style={circleStyle}
          onClick={() => fullProfileImageView && setShowPopup(true)}
        >
          <img src={profile_image} alt={initials} onError={() => setValidImage(false)} />
        </div>
        {fullProfileImageView && showPopup && renderPopup()}
      </>
    );
  }

  return (
    <div className={containerClasses} style={circleStyle}>
      <div className={`avatar-text ${textClassName}`} style={textStyle}>
        {initials}
      </div>
    </div>
  );
};

export default Avatar;
